import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "../../components/common/Button";
import SidebarToggle from "../../components/Doctor/SidebarToggle";
import AddDoctorModal from "../../components/Doctor/Portal/AddDoctorModal";
import apiAxios from "../../services/apiAxios";
import toast from "react-hot-toast";
import { logout } from "../../redux/Reducer/authSlice";

const WorkspaceHub = () => {
  const [doctors, setDoctors] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const operationalDoctor = doctors.find(d => d.isOperational);

  const loadDoctors = () => {
    const stored = JSON.parse(localStorage.getItem("loggedInDoctors") || "[]");

    // Auto-clear corrupt/old cached accounts missing clinic data
    if (stored.length > 0 && stored.some(d => !d.__v)) {
      localStorage.removeItem("loggedInDoctors");
      window.location.reload();
      return;
    }

    setDoctors(stored);
  };

  useEffect(() => {
    loadDoctors();
  }, []);

  const handleOperate = (did) => {
    const updated = doctors.map(d => ({
      ...d,
      isOperational: d.DID === did
    }));
    setDoctors(updated);
    localStorage.setItem("loggedInDoctors", JSON.stringify(updated));
    // Immediately navigate to studio to apply operational change
    navigate("/studio");
  };

  const handleLogoutAccount = async (did, isPrimary) => {
    const loadingToast = toast.loading(isPrimary ? "Logging out of all sessions..." : "Logging out...");
    
    try {
      const account = doctors.find(d => d.DID === did);
      if (!account) {
        toast.dismiss(loadingToast);
        return;
      }

      if (isPrimary) {
        // Global Logout: Close all sessions before leaving
        const logoutPromises = doctors.map(acc => 
          apiAxios.patch(
            `api/doctor/update-doctor-status`,
            { currentDoctorStatus: "CLOSED" },
            { headers: { Authorization: `Bearer ${acc.token}` }, timeout: 8000 }
          ).catch(err => console.warn(`Failed to close session for ${acc.DID}`, err))
        );

        await Promise.allSettled(logoutPromises);
        
        localStorage.clear();
        dispatch(logout());
        toast.success("All sessions closed. Redirecting...", { id: loadingToast });
        
        setTimeout(() => {
          window.location.href = process.env.REACT_APP_DOCTOR_URL;
        }, 1000);
        return;
      }

      // Single Secondary Logout
      await apiAxios.patch(
        `api/doctor/update-doctor-status`,
        { currentDoctorStatus: "CLOSED" },
        { headers: { Authorization: `Bearer ${account.token}` }, timeout: 8000 }
      );

      const updated = doctors.filter(d => d.DID !== did);
      
      // If we just logged out the operational doctor, fallback to primary
      if (!updated.some(d => d.isOperational)) {
        const primary = updated.find(d => d.isPrimary);
        if (primary) primary.isOperational = true;
      }

      setDoctors(updated);
      localStorage.setItem("loggedInDoctors", JSON.stringify(updated));
      toast.success("Account logged out successfully", { id: loadingToast });
    } catch (error) {
      console.error("Logout error:", error);
      
      if (!isPrimary) {
        // If secondary logout fails, we still remove it locally to avoid blocking the user
        const updated = doctors.filter(d => d.DID !== did);
        setDoctors(updated);
        localStorage.setItem("loggedInDoctors", JSON.stringify(updated));
        toast.error("Status update failed, but account removed locally.", { id: loadingToast });
      } else {
        toast.error("Logout failed. Please check your connection.", { id: loadingToast });
      }
    }
  };


  return (
    <div className="w-full h-full min-h-[calc(100vh-2rem)] flex flex-col bg-[#F8F9FA] rounded-3xl shadow-sm overflow-hidden p-8 relative isolate">
      {/* Mesh Gradient Background */}
      <div className="absolute inset-0 -z-10 pointer-events-none opacity-60">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-primary-100/40 blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-[0%] right-[-5%] w-[40%] h-[40%] rounded-full bg-green-100/40 blur-[120px] animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-[20%] right-[10%] w-[30%] h-[30%] rounded-full bg-blue-100/30 blur-[100px] animate-pulse" style={{ animationDelay: '4s' }}></div>
      </div>

      <div className="flex items-center gap-3.5 mb-10">
        <SidebarToggle />

        {/* Brand Logo & Wordmark + Page Name */}
        <div className="flex flex-col gap-1 shrink-0 justify-center">
          <div className="flex items-center gap-1.5 shrink-0">
            <img src="/logo.png" alt="logo" className="h-[18px] sm:h-[22px] w-auto object-contain shrink-0" />
            <img src="/logo.svg" alt="Hyperapt" className="h-[15px] sm:h-[18px] w-auto max-w-[90px] sm:max-w-[105px] object-contain shrink-0" />
          </div>
          <span className="text-[17px] sm:text-[21px] font-black text-gray-900 tracking-tight leading-none">
            Workspace Hub
          </span>
        </div>

        {/* Vertical Partition */}
        <div className="h-7 w-[1px] bg-gray-200 hidden sm:block"></div>

        <div>
          <p className="text-gray-500 text-[13px]">
            Manage accounts and switch between active workspaces.
          </p>
        </div>
      </div>

      <div className="flex-1 flex flex-col">
        {/* Active Doctor Tile - Glassmorphic */}
        {operationalDoctor && (
          <div className="w-full mb-8 p-5 rounded-3xl border border-gray-100 bg-white/40 backdrop-blur-xl shadow-sm flex items-center justify-between">
            <div className="flex items-center gap-5">
              <div className="w-12 h-12 rounded-2xl bg-green-50 flex items-center justify-center">
                <span className="text-green-600 text-xl">👤</span>
              </div>
              <div>
                <p className="text-[10px] font-semibold text-green-600 uppercase tracking-[0.1em] mb-0.5 opacity-80">Currently Operating</p>
                <h2 className="text-xl font-bold text-gray-900 leading-none">
                  Dr. {operationalDoctor.name} {operationalDoctor.surname}
                </h2>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-2.5 bg-green-50 px-3.5 py-1.5 rounded-xl border border-green-100">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              <span className="text-green-700 font-semibold text-[11px] uppercase tracking-wide">Live Workspace</span>
            </div>
          </div>
        )}

        {doctors.length > 0 ? (
          <div className="flex-1">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-[16px] font-bold text-gray-800 tracking-tight">Available Workspaces</h2>
              <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest bg-gray-50 px-3 py-1 rounded-full border border-gray-100">
                {doctors.length} {doctors.length === 1 ? 'Account' : 'Accounts'}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pb-8">
              {doctors.map((doctor) => {
                const isPrimary = doctor.isPrimary;
                const isOperational = doctor.isOperational;

                return (
                  <div
                    key={doctor.DID}
                    className={`group rounded-3xl border transition-all duration-300 flex flex-col relative overflow-hidden h-full
                      ${isOperational
                        ? 'border-green-400 bg-white shadow-xl shadow-green-500/10 ring-1 ring-green-400/20'
                        : 'border-gray-200 bg-white shadow-sm hover:shadow-md hover:-translate-y-1 hover:border-primary-100'}
                    `}
                  >
                    {/* Visual Accent */}
                    <div className={`h-1.5 w-full ${isOperational ? 'bg-green-400' : isPrimary ? 'bg-primary-400' : 'bg-gray-200'} transition-colors duration-300`} />

                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex justify-between items-start mb-5">
                        <div className="flex gap-2 items-center">
                          {isPrimary && (
                            <span className="bg-primary-50 text-primary-600 text-[9px] font-bold px-2 py-0.5 rounded-md border border-primary-100 uppercase tracking-wider">
                              Primary
                            </span>
                          )}
                          {isOperational && (
                            <span className="bg-green-50 text-green-600 text-[9px] font-bold px-2 py-0.5 rounded-md border border-green-100 uppercase tracking-wider">
                              Active
                            </span>
                          )}
                        </div>

                        {!isPrimary && (
                          <button
                            onClick={() => handleLogoutAccount(doctor.DID, isPrimary)}
                            className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all duration-300 md:opacity-0 group-hover:opacity-100"
                            title="Logout Account"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
                          </button>
                        )}
                      </div>

                      <div className="mb-6">
                        <h3 className="text-[18px] font-bold text-gray-900 mb-4 leading-tight">
                          Dr. {doctor.name} {doctor.surname}
                        </h3>

                        <div className="space-y-1.5">
                          <div className="flex items-start gap-3">
                            <span className="text-[14px] w-5 h-5 flex items-center justify-center shrink-0 opacity-70">🏥</span>
                            <span className="text-[12px] text-gray-600 font-semibold line-clamp-1 leading-relaxed">
                              {doctor.clinicName || 'No Clinic Name'}
                            </span>
                          </div>

                          <div className="flex items-start gap-3">
                            <span className="text-[14px] w-5 h-5 flex items-center justify-center shrink-0 opacity-70">📍</span>
                            <span className="text-[11px] text-gray-400 font-medium line-clamp-1 leading-relaxed">
                              {doctor.address || 'Address not provided'}
                            </span>
                          </div>

                          <div className="flex items-center gap-3">
                            <span className="text-[14px] w-5 h-5 flex items-center justify-center shrink-0 opacity-70">📞</span>
                            <span className="text-[11px] text-gray-400 font-medium">
                              {doctor.phoneNumber}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="mt-auto -mx-6 -mb-6 overflow-hidden rounded-b-3xl">
                        {!isOperational ? (
                          <button
                            onClick={() => handleOperate(doctor.DID)}
                            className="w-full py-4 text-[13px] font-bold text-white bg-primary-600 hover:bg-primary-700 transition-all flex items-center justify-center gap-1.5"
                          >
                            <span>Operate</span>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                          </button>
                        ) : (
                          <div className="w-full py-4 text-[13px] font-bold text-green-700 bg-green-50 border-t border-green-100 flex items-center justify-center gap-2">
                            <span className="relative flex h-2 w-2">
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                            </span>
                            <span>Currently Operating</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center min-h-[300px]">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl opacity-50">👥</span>
              </div>
              <h3 className="text-gray-900 font-bold mb-1">No Accounts Found</h3>
              <p className="text-gray-500 text-[13px]">Add a doctor account to get started.</p>
            </div>
          </div>
        )}

        <div className="mt-auto pt-8 flex justify-center border-t border-gray-100/50">
          <Button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 bg-white text-gray-800 px-8 py-3 rounded-2xl font-semibold text-[13px] border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm active:scale-95"
          >
            <span className="text-lg leading-none mb-0.5">+</span> Add Doctor Account
          </Button>
        </div>
      </div>

      {showModal && (
        <AddDoctorModal
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            loadDoctors();
          }}
        />
      )}

      <style dangerouslySetInnerHTML={{
        __html: `
        .scrollbar-hide::-webkit-scrollbar {
            display: none;
        }
      `}} />
    </div>
  );
};

export default WorkspaceHub;
